import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import Modal from '../Modal/Modal';

const ERROR = 'error';
const GENERIC = 'generic';
const INFO = 'info';
const OK = 'success';
const WARNING = 'warning';

/**
 * `Dialog` is the component for displaying OK, warning and error dialogs.
 *
 * You only need to use this component directly when you want to build your own custom dialogs, as {@link Application}
 * provides a set of static functions for easy display of the standard dialog types.
 *
 * The dialog buttons are displayed in the order "Discard", "Cancel", "OK" on all platforms except Windows. On Windows the
 * buttons will be reversed to "OK", "Cancel", "Discard" to be more consistent with Windows dialogs.
 *
 * Use the OK dialog sparingly. You should normally not display an OK dialog after successful operations. Instead you
 * should use a warning dialog before dangerous operations are initiated. If the operation takes a long time to execute in
 * the background you could use a {@link Notice} message to notify the user of successful completion.

 * The dialog will be placed inside a {@link Modal} component.
 *
 * #### Write consistent dialog messages
 * Good error and warning messages should:
 * 1. Let the user know what the problem is in a non-technical manner.
 * 2. Let the user know how the problem can be fixed.
 * 3. Be written in a tone that is consistent with other dialog messages. Write for non-technical humans.
 *
 * @property {string} cancel The text on the **Cancel** button. Defaults to no text (button will not show).
 * @property {function} cancelCallback The callback function that indicates that the **Cancel** button was clicked.
 * @property {node} children The content of your custom dialog. The content will be displayed below the icon and text if
 * these props are provided.
 * @property {string} className The class name of your dialog. Will be appended "core-dialog".
 * @property {boolean} closeOnClickOutside If set to `true`, the dialog will close when the user clicks outside the dialog.
 * This will be the same as cancelling the dialog. Defaults to `false`.
 * @property {string} discard The text on the **Discard** button. Defaults to no text (button will not show).
 * @property {function} discardCallback The callback function that indicates that the **Discard** button was clicked.
 * @property {string} message The message of your dialog. Defaults to "".
 * @property {boolean} discardDisabled If set to `true`, the **Discard** button will be disabled. Defaults to `false`.
 * @property {string} ok The text on the **OK** button. Defaults to "OK".
 * @property {function} okCallback The callback function that indicates that the **OK** button was clicked.
 * @property {boolean} okDisabled If set to `true`, the **OK** button will be disabled. Defaults to `false`.
 * @property {string} platform The browser navigator string that will be used when determining the platform. If no platform
 * is set, the component will get the platform from the browser navigator object.
 * @property {string} title The title of your dialog. No title is set by default.
 * @property {string} type One of the following types `Dialog.TYPE_OK`, `Dialog.TYPE_WARNING`, `Dialog.TYPE_ERROR`,
 * `Dialog.TYPE_INFO` or `Dialog.TYPE_GENERIC` (any other type).
 *
 * @see Application, Modal, Notice
 */
export default class Dialog extends Component {

  static displayName = 'Dialog';

  static TYPE_ERROR = ERROR;
  static TYPE_GENERIC = GENERIC;
  static TYPE_INFO = INFO;
  static TYPE_OK = OK;
  static TYPE_WARNING = WARNING;

  static propTypes = {
    cancel: PropTypes.string,
    cancelCallback: PropTypes.func,
    children: PropTypes.node,
    className: PropTypes.string,
    closeOnClickOutside: PropTypes.bool,
    discard: PropTypes.string,
    discardCallback: PropTypes.func,
    discardDisabled: PropTypes.bool,
    message: PropTypes.string,
    ok: PropTypes.string,
    okCallback: PropTypes.func,
    okDisabled: PropTypes.bool,
    platform: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
  };

  static defaultProps = {
    closeOnClickOutside: false,
    discardDisabled: false,
    message: '',
    ok: 'OK',
    okCallback: () => {},
    okDisabled: false,
    cancelCallback: () => {},
    type: GENERIC,
  };

  constructor(props, context) {
    super(props, context);

    const platform = props.platform || (window && window.navigator && (window.navigator.platform || window.navigator.oscpu))
      || '';
    this.state = {
      swapButtons: platform.indexOf('Windows') > -1
    };
  }

  componentDidMount() {
    document.addEventListener('keydown', this.onKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
  }

  onClose = e => {
    if (this.props.cancelCallback) {
      return this.props.cancelCallback(e);
    }
    return this.props.okCallback(e);
  };

  onKeyDown = e => {
    if (e.which === 13) {
      e.preventDefault();
      e.stopPropagation();
      return this.props.okCallback(e);
    }
    return true;
  };

  render() {
    const { children, className, closeOnClickOutside, ok, okDisabled, cancel, discard, discardDisabled, type } = this.props;
    let { title, message } = this.props;
    let btn1;
    let btn2;
    let btn3;
    let num = 0;

    if (title) {
      title = <h2>{title}</h2>;
    }
    if (discard) {
      btn1 = <Button className="discard tertiary" onClick={this.props.discardCallback} disabled={discardDisabled}>{discard}</Button>;
      ++num;
    }
    if (cancel) {
      btn2 = <Button className="cancel tertiary" onClick={this.props.cancelCallback}>{cancel}</Button>;
      ++num;
    }
    if (ok) {
      btn3 = <Button className="ok primary" onClick={this.props.okCallback} disabled={okDisabled}>{ok}</Button>;
      ++num;
    }
    if (message) {
      message = <p dangerouslySetInnerHTML={{ __html: message }}/>; // eslint-disable-line react/no-danger
    }
    if (this.state.swapButtons) {
      [btn1, btn2, btn3] = [btn3, btn1, btn2];
    }
    return (
      <Modal className="core-dialog-wrapper" onClose={this.onClose} closeOnClickOutside={closeOnClickOutside}>
        <div className={cx('core-dialog', `type-${type}`, className)}>
          <div className="core-dialog-content">
            <Icon className="core-dialog-icon" name={type}/>
            {title}
            {message}
            {children}
          </div>
          <div className={cx('buttons', { 'ok-discard-cancel': ok && discard && cancel, 'center-button': num === 1 })}>
            {btn1}
            {btn2}
            {btn3}
          </div>
        </div>
      </Modal>
    );
  }

}
